import { StrictMode } from 'react'
import { createRoot } from 'react-dom/client'
import { createBrowserRouter, RouterProvider, Navigate, useParams } from 'react-router-dom'
import { QueryClient, QueryClientProvider } from '@tanstack/react-query'
import { ThemeProvider } from './contexts/ThemeContext'
import { DataExplorerProvider } from './contexts/DataExplorerContext'
import { FormatLocaleProvider } from './contexts/FormatLocaleContext'
import { Toaster } from './components/ui/sonner'
import './index.css'
import App from './App.tsx'

const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      staleTime: 5 * 60 * 1000, // 5 minutes
      retry: (failureCount, error: any) => {
        if (error?.code === 404) {
          return false
        }
        return failureCount < 3
      },
    },
  },
})
import Home from './pages/Home.tsx'
import Metering from './pages/Metering.tsx'
import MeteringThreadDetailPage from './pages/MeteringThreadDetail.tsx'
import UseCasesPage from './pages/UseCases.tsx'
import AuthenticatedRootPage from './pages/AuthenticatedRootPage.tsx'
import UnauthenticatedRootPage from './pages/UnauthenticatedRootPage.tsx'
import Login from './pages/Login.tsx'
import Agents from './pages/Agents.tsx'
import Data from './pages/Data.tsx'
import Sources from './pages/data/Sources.tsx'
import SourceDetail from './pages/data/SourceDetail.tsx'
import SyncDetail from './pages/data/SyncDetail.tsx'
import Cleaning from './pages/data/Cleaning.tsx'
import Metrics from './pages/data/Metrics.tsx'
import Entities from './pages/data/Entities.tsx'
import KnowledgeGraph from './pages/data/KnowledgeGraph.tsx'
import Context from './pages/data/Context.tsx'
import Artifacts, { ArtifactDetail } from './pages/Artifacts.tsx'
import KnowledgeBase from './pages/KnowledgeBase.tsx'
import Requests from './pages/requests/Requests.tsx'
import RequestDetail from './pages/requests/RequestDetail.tsx'
import Insights from './pages/Insights.tsx'
import ChartDetail from './pages/analysis/ChartDetail.tsx'
import DashboardDetail from './pages/analysis/DashboardDetail.tsx'
import ConversationPage from './pages/ellie/ConversationPage.tsx'
import Develop from './pages/develop/Develop.tsx'
import Nodes from './pages/develop/Nodes.tsx'
import NodeDetail from './pages/develop/NodeDetail.tsx'
import EntityDetail from './pages/develop/EntityDetail.tsx'
import DevelopEntities from './pages/develop/Entities.tsx'
import DevelopMetrics from './pages/develop/Metrics.tsx'
import MetricDetail from './pages/develop/MetricDetail.tsx'
import Dimensions from './pages/develop/Dimensions.tsx'
import DimensionDetail from './pages/develop/DimensionDetail.tsx'
import DangerZone from './pages/develop/DangerZone.tsx'
import SemanticGaps from './pages/develop/SemanticGaps.tsx'
import Tasks from './pages/develop/Tasks.tsx'
import ParkedTasks from './pages/develop/ParkedTasks.tsx'
import Supersets from './pages/develop/Supersets.tsx'
import { RequireRole } from './components/RequireRole.tsx'
import { RequireTenantMember } from './components/RequireTenantMember.tsx'
import { ReadOnlyInProduction } from './components/ReadOnlyInProduction.tsx'
import Analytics from './pages/analysis/Analytics.tsx'
import Goals from './pages/analysis/Goals.tsx'
import ElliePage from './pages/Ellie.tsx'
import RuthiePage from './pages/Ruthie.tsx'
import RuthieConversationPage from './pages/ruthie/ConversationPage.tsx'
import RuthieGuard from './components/RuthieGuard.tsx'
import FeatureFlagGuard from './components/FeatureFlagGuard.tsx'
import { FeatureFlag } from './services/featureFlag.service.ts'
import SemanticLayerDiagram from './pages/develop/SemanticLayerDiagram.tsx'
import SemanticLayerLayout from './pages/develop/SemanticLayerLayout.tsx'
import EmbedSessions from './pages/usage/EmbedSessions.tsx'
import EmbedSessionDetail from './pages/usage/EmbedSessionDetail.tsx'
import {
  USAGE_ANALYTICS_DEFAULT_PATH,
  USAGE_ANALYTICS_ELLIE_USAGE_PATH,
} from './components/UsageAnalyticsPageHeader.tsx'
import SettingsPage from './pages/Settings.tsx'
import SupersetResolutionLogs from './pages/SupersetResolutionLogs.tsx'
import UsersPage from './pages/Users.tsx'
import InstallTemplates from './pages/templates/InstallTemplates.tsx'
import MyTemplates from './pages/templates/MyTemplates.tsx'
import ExportTemplate from './pages/templates/ExportTemplate.tsx'
import TenantArchived from './pages/TenantArchived.tsx'
import ExtLogin from './pages/ExtLogin.tsx'
import SystemSession from './pages/SystemSession.tsx'
import SecureConfigFillPage from './pages/SecureConfigFillPage.tsx'
import FlatFileUploadPage from './pages/FlatFileUploadPage.tsx'
import AuthCompletePage from './pages/AuthCompletePage.tsx'
import AuthShareLandingPage from './pages/AuthShareLandingPage.tsx'
import Embed from './pages/Embed.tsx'
import EmbedDemo from './pages/EmbedDemo.tsx'
import DemoConfigsPage from './pages/DemoConfigs.tsx'
import Logout from './pages/Logout.tsx'
import TenantNotFound from './pages/TenantNotFound.tsx'
import NoDataAccess from './pages/NoDataAccess.tsx'
import SsoPrelogin from './pages/SsoPrelogin.tsx'
import DocsLayout from './pages/docs/DocsLayout.tsx'
import DocPage from './pages/docs/DocPage.tsx'
import FivetranKeypairUpgrade from './pages/FivetranKeypairUpgrade.tsx'
import CliAuth from './pages/CliAuth.tsx'
import GithubAppCallback from './pages/GithubAppCallback'
import McpConsent from './pages/McpConsent.tsx'

const DATA_MODEL_PATH = '/data-model'
const DATA_MODEL_DIAGRAM_PATH = '/data-model/diagram'

// Sends an old detail URL (e.g. /develop/entities/:id) to its new home, keeping the record id.
function LegacyDetailRedirect({ base, param = 'id' }: { base: string; param?: string }) {
  const params = useParams()
  return <Navigate to={`${base}/${params[param]}`} replace />
}

// Sends everything under an old prefix to its new home, e.g. /develop/semantic-layer/metrics/x
// -> /data-model/metrics/x. Matched with a splat route, so it also covers the bare prefix.
function LegacyPrefixRedirect({ base }: { base: string }) {
  const rest = useParams()['*']
  return <Navigate to={rest ? `${base}/${rest}` : base} replace />
}

const router = createBrowserRouter([
  {
    path: '/',
    element: <App />,
    children: [
      {
        // Consent flows. Same authentication and tenant gating as the main app
        // — login, archived/not-found, no-data-access — but no sidebar: each is
        // a single focused decision reached from outside the app, often in a
        // popup, and navigation invites wandering off mid-authorization.
        element: <AuthenticatedRootPage chromeless />,
        children: [
          {
            // The `plb` CLI login handoff.
            path: 'cli-auth',
            element: <CliAuth />,
          },
          {
            // Where GitHub returns after the Pliable App is installed/authorized.
            // Path is fixed by the App's Setup + Callback URL config, and matches
            // the one the old frontend used so an existing App needs no change.
            path: 'onboarding/github',
            element: <GithubAppCallback />,
          },
          {
            // The authorization endpoint advertised in our OAuth metadata;
            // MCP clients send the browser here. Deliberately not under /mcp —
            // that prefix belongs to the MCP transport and is intercepted by it.
            path: 'oauth/authorize',
            element: <McpConsent />,
          },
        ],
      },
      {
        element: <AuthenticatedRootPage />,
        children: [
          {
            index: true,
            element: <Home />,
          },
          {
            path: 'usecases',
            element: <UseCasesPage />,
          },
          {
            // Admin-only ops page; intentionally not in the sidebar nav
            path: 'admin/fivetran-keypair-upgrade',
            element: <FivetranKeypairUpgrade />,
          },
          {
            path: 'ellie',
            element: <ElliePage />,
          },
          {
            path: 'ruthie',
            element: (
              <RuthieGuard>
                <RuthiePage />
              </RuthieGuard>
            ),
          },
          {
            path: 'agents',
            element: <Agents />,
          },
          {
            // Legacy paths: the semantic layer (and the retired glossary) are now the Data Model.
            path: 'semantic-layer',
            element: <Navigate to={DATA_MODEL_DIAGRAM_PATH} replace />,
          },
          {
            path: 'semantic-layer/*',
            element: <Navigate to={DATA_MODEL_DIAGRAM_PATH} replace />,
          },
          {
            path: 'glossary',
            element: <Navigate to={DATA_MODEL_DIAGRAM_PATH} replace />,
          },
          {
            path: 'knowledge-base',
            element: (
              <RequireTenantMember>
                <KnowledgeBase />
              </RequireTenantMember>
            ),
          },
          {
            path: 'requests',
            element: (
              <RequireTenantMember>
                <Requests />
              </RequireTenantMember>
            ),
          },
          {
            path: 'requests/:id',
            element: (
              <RequireTenantMember>
                <RequestDetail />
              </RequireTenantMember>
            ),
          },
          {
            path: 'knowledge-base/article/:id',
            element: (
              <RequireTenantMember>
                <KnowledgeBase />
              </RequireTenantMember>
            ),
          },
          {
            path: 'artifacts',
            element: <Artifacts />,
          },
          {
            path: 'artifacts/:id',
            element: <ArtifactDetail />,
          },
          {
            path: 'settings',
            element: <SettingsPage />,
          },
          {
            // Usage Analytics (admin only): one route per tab.
            path: 'usage-analytics',
            element: <Navigate to={USAGE_ANALYTICS_DEFAULT_PATH} replace />,
          },
          {
            path: 'usage-analytics/embed-sessions',
            element: (
              <RequireRole role="Admin">
                <EmbedSessions />
              </RequireRole>
            ),
          },
          {
            path: 'usage-analytics/embed-sessions/:jti',
            element: (
              <RequireRole role="Admin">
                <EmbedSessionDetail />
              </RequireRole>
            ),
          },
          {
            path: 'usage-analytics/ellie-usage',
            element: (
              <RequireRole role="Admin">
                <Metering />
              </RequireRole>
            ),
          },
          {
            path: 'usage-analytics/ellie-usage/threads/:thread_id',
            element: (
              <RequireRole role="Admin">
                <MeteringThreadDetailPage />
              </RequireRole>
            ),
          },
          {
            // Admin-only: create/manage branded /demo/:id sales-demo landing pages.
            // Intentionally not in the sidebar nav for now — internal-only tool.
            path: 'demo-configs',
            element: (
              <RequireRole role="Admin">
                <DemoConfigsPage />
              </RequireRole>
            ),
          },
          // Legacy paths: LLM Metering moved into Usage Analytics as the Ellie Usage tab.
          {
            path: 'metering/llm',
            element: <Navigate to={USAGE_ANALYTICS_ELLIE_USAGE_PATH} replace />,
          },
          {
            path: 'metering/llm/threads/:thread_id',
            element: <LegacyDetailRedirect base={`${USAGE_ANALYTICS_ELLIE_USAGE_PATH}/threads`} param="thread_id" />,
          },
          {
            path: 'superset-resolution-logs',
            element: <SupersetResolutionLogs />,
          },
          {
            path: 'users',
            element: <UsersPage />,
          },
          {
            path: 'sources',
            element: <Sources />,
          },
          {
            path: 'sources/:sourceId',
            element: <SourceDetail />,
          },
          {
            path: 'sources/:sourceId/syncs/:dataloadId',
            element: <SyncDetail />,
          },
          {
            path: 'sources/:sourceId/:nodeId',
            element: <SourceDetail />,
          },
          {
            path: 'data',
            element: <Data />,
            children: [
              {
                index: true,
                element: <Navigate to="/data/cleaning" replace />,
              },
              {
                path: 'cleaning',
                element: <Cleaning />,
              },
              {
                path: 'metrics',
                element: <Metrics />,
              },
              {
                path: 'entities',
                element: <Entities />,
              },
              {
                path: 'knowledge-graph',
                element: <KnowledgeGraph />,
              },
              {
                path: 'context',
                element: <Context />,
              },
            ],
          },
          {
            path: 'insights',
            element: <Insights />,
            children: [
              // Detail routes only — the /insights list pages were retired in
              // favour of /analytics. The dashboard detail path is the target
              // embedded-analytics links resolve to, so it has to stay put.
              {
                index: true,
                element: <Navigate to="/analytics" replace />,
              },
              {
                path: 'charts/:chartId',
                element: <ChartDetail />,
              },
              {
                path: 'dashboards/:dashboardId',
                element: <DashboardDetail />,
              },
            ],
          },
          {
            path: 'ellie/conversation/:id',
            element: <ConversationPage />,
          },
          {
            path: 'ruthie/conversation/:id',
            element: (
              <RuthieGuard>
                <RuthieConversationPage />
              </RuthieGuard>
            ),
          },
          {
            path: 'analytics',
            element: <Analytics />,
          },
          {
            path: 'analytics/charts/:chartId',
            element: <ChartDetail />,
          },
          {
            path: 'analytics/dashboards/:dashboardId',
            element: <DashboardDetail />,
          },
          {
            // Top level, not under /analytics — the sidebar marks an item active
            // with startsWith(path + '/'), so a nested route would light up the
            // Analytics entry instead of Goals.
            path: 'goals',
            element: <Goals />,
          },
          {
            // The data model: the models feeding the pipeline plus the entities, metrics and
            // dimensions built on them. One layout (quick nav + content pane) for all of them,
            // so the nav's search and scroll survive navigation between any two.
            path: 'data-model',
            element: <SemanticLayerLayout />,
            children: [
              {
                index: true,
                element: <Navigate to={DATA_MODEL_DIAGRAM_PATH} replace />,
              },
              {
                path: 'diagram',
                element: <SemanticLayerDiagram />,
              },
              {
                /* Every model at once. Quick Nav splits them (below); this is how a
                   tenant with no semantic layer reaches them. */
                path: 'models',
                element: <Nodes />,
              },
              {
                path: 'sources',
                element: <Nodes kind="source" />,
              },
              {
                path: 'transformations',
                element: <Nodes kind="transformation" />,
              },
              {
                path: 'models/:id',
                element: (
                  <ReadOnlyInProduction>
                    <NodeDetail />
                  </ReadOnlyInProduction>
                ),
              },
              {
                path: 'entities',
                element: <DevelopEntities />,
              },
              {
                path: 'entities/:id',
                element: (
                  <ReadOnlyInProduction>
                    <EntityDetail />
                  </ReadOnlyInProduction>
                ),
              },
              {
                path: 'metrics',
                element: <DevelopMetrics />,
              },
              {
                path: 'metrics/:id',
                element: (
                  <ReadOnlyInProduction>
                    <MetricDetail />
                  </ReadOnlyInProduction>
                ),
              },
              {
                path: 'dimensions',
                element: <Dimensions />,
              },
              {
                path: 'dimensions/:id',
                element: (
                  <ReadOnlyInProduction>
                    <DimensionDetail />
                  </ReadOnlyInProduction>
                ),
              },
            ],
          },
          {
            path: 'develop',
            element: <Develop />,
            children: [
              {
                index: true,
                element: <Navigate to={DATA_MODEL_PATH} replace />,
              },
              // Legacy paths, kept so existing links/bookmarks keep working. The data model
              // moved out from under /develop.
              {
                path: 'modeling/*',
                element: <LegacyPrefixRedirect base={`${DATA_MODEL_PATH}/models`} />,
              },
              {
                path: 'semantic-layer/*',
                element: <LegacyPrefixRedirect base={DATA_MODEL_PATH} />,
              },
              {
                path: 'entities',
                element: <Navigate to={`${DATA_MODEL_PATH}/entities`} replace />,
              },
              {
                path: 'entities/:id',
                element: <LegacyDetailRedirect base={`${DATA_MODEL_PATH}/entities`} />,
              },
              {
                path: 'metrics',
                element: <Navigate to={`${DATA_MODEL_PATH}/metrics`} replace />,
              },
              {
                path: 'metrics/:id',
                element: <LegacyDetailRedirect base={`${DATA_MODEL_PATH}/metrics`} />,
              },
              {
                path: 'dimensions',
                element: <Navigate to={`${DATA_MODEL_PATH}/dimensions`} replace />,
              },
              {
                path: 'dimensions/:id',
                element: <LegacyDetailRedirect base={`${DATA_MODEL_PATH}/dimensions`} />,
              },
              {
                path: 'gaps',
                element: (
                  <FeatureFlagGuard flag={FeatureFlag.VIEW_SEMANTIC_GAPS}>
                    <SemanticGaps />
                  </FeatureFlagGuard>
                ),
              },
              {
                path: 'tasks',
                element: <Tasks />,
              },
              {
                path: 'tasks/parked',
                element: <ParkedTasks />,
              },
              {
                path: 'supersets',
                element: (
                  <RequireRole role="Builder">
                    <Supersets />
                  </RequireRole>
                ),
              },
              {
                path: 'danger-zone',
                element: <DangerZone />,
              },
            ],
          },
          {
            // Redirect legacy /dashboards/:id URLs (used by screengrabber) to new path
            path: 'dashboards/:dashboardId',
            element: <DashboardDetail />,
          },
          {
            path: 'templates/install',
            element: <InstallTemplates />,
          },
          {
            path: 'templates/admin',
            element: <MyTemplates />,
          },
          {
            path: 'templates/export',
            element: <ExportTemplate />,
          },
          {
            path: 'templates/edit/:templateId',
            element: <ExportTemplate />,
          },
        ],
      },
      {
        path: 'do/logout',
        element: <Logout />,
      },
      {
        path: 'logout',
        element: <Navigate to="/do/logout" replace />,
      },
      {
        path: 'tenant-archived',
        element: <TenantArchived />,
      },
      {
        path: 'tenant-not-found',
        element: <TenantNotFound />,
      },
      {
        path: 'no-data-access',
        element: <NoDataAccess />,
      },
      {
        path: 'system-session',
        element: <SystemSession />,
      },
      {
        path: 'sso-prelogin',
        element: <SsoPrelogin />,
      },
      {
        path: 'source/secure-config/:code',
        element: <SecureConfigFillPage />,
      },
      {
        path: 'source/flat-file-upload/:code',
        element: <FlatFileUploadPage />,
      },
      {
        path: 'source/auth-complete',
        element: <AuthCompletePage />,
      },
      {
        path: 'source/connect/:code',
        element: <AuthShareLandingPage />,
      },
      {
        // Branded sales-demo landing page (Admin-managed DemoConfig).
        // See src/pages/EmbedDemo.tsx.
        path: 'demo/:id',
        element: <EmbedDemo />,
      },
      {
        // Embedded-analytics iframe landing. Exchanges the one-time code
        // (from POST /api/v1/ext/init-session) for a JWT and redirects to
        // the target (e.g. /insights/dashboards/<id>). See src/pages/Embed.tsx.
        path: 'embed',
        element: <Embed />,
      },
      {
        path: 'docs',
        element: <DocsLayout />,
        children: [
          {
            index: true,
            element: <Navigate to="/docs/connecting-to-snowflake" replace />,
          },
          {
            path: ':slug',
            element: <DocPage />,
          },
        ],
      },
      {
        element: <UnauthenticatedRootPage />,
        children: [
          {
            path: 'login',
            element: <Login />,
          },
          {
            path: 'ext-login',
            element: <ExtLogin />,
          },
        ],
      },
      {
        path: '*',
        element: <Navigate to="/" replace />,
      },
    ],
  },
])

createRoot(document.getElementById('root')!).render(
  <StrictMode>
    <ThemeProvider>
      <QueryClientProvider client={queryClient}>
        <FormatLocaleProvider>
          <DataExplorerProvider>
            <RouterProvider router={router} />
            <Toaster />
          </DataExplorerProvider>
        </FormatLocaleProvider>
      </QueryClientProvider>
    </ThemeProvider>
  </StrictMode>,
)
